import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useRegion, REGIONS } from "@/context/RegionContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Heart, List, User, SignOut, Gauge, Bookmark } from "@phosphor-icons/react";
import { RegionSwitcher } from "@/components/RegionSwitcher";

const navItems = [
  { label: "Jobs", to: "/jobs" },
  { label: "Career Advice", to: "/career-advice" },
  { label: "Employers", to: "/employers" },
  { label: "About", to: "/about" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (to) => location.pathname === to;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-8">
        <div className="flex items-center gap-10">
          <Link to="/" data-testid="nav-logo-link">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-8 lg:flex">
            {navItems.map((item, i) => (
              <Link
                key={i}
                to={item.to}
                className={`relative text-sm font-medium transition-colors hover:text-primary ${
                  isActive(item.to) ? "text-primary" : "text-foreground/70"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <RegionSwitcher />
          {user && user !== false && (
            <Link
              to="/saved"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground/70 transition-colors hover:text-primary"
              data-testid="nav-saved-link"
            >
              <Heart size={18} weight="regular" /> Saved
            </Link>
          )}

          {user && user !== false ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-9 items-center gap-2 rounded-full border border-border bg-white px-3 text-sm font-medium transition-colors hover:border-primary"
                  data-testid="nav-user-menu"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </span>
                  <span className="max-w-[100px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {user.role === "admin" && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin-link">
                      <Gauge className="mr-2" size={16} /> Admin Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate("/saved")}>
                  <Bookmark className="mr-2" size={16} /> Saved Jobs
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                  <SignOut className="mr-2" size={16} /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => navigate("/login")}
                data-testid="nav-signin-btn"
              >
                Sign in
              </Button>
              <Button
                className="rounded-full"
                onClick={() => navigate("/register")}
                data-testid="nav-post-job-btn"
              >
                Get Started
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
        >
          <List size={26} />
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-white px-4 py-4 md:hidden" data-testid="nav-mobile-menu">
          <nav className="flex flex-col gap-3">
            <RegionSwitcher className="w-fit" />
            {navItems.map((item, i) => (
              <Link key={i} to={item.to} className="text-sm font-medium" onClick={() => setOpen(false)}>
                {item.label}
              </Link>
            ))}
            <div className="mt-2 flex flex-col gap-2">
              {user && user !== false ? (
                <>
                  <Link to="/saved" className="text-sm font-medium" onClick={() => setOpen(false)}>Saved Jobs</Link>
                  {user.role === "admin" && (
                    <Link to="/admin" className="text-sm font-medium" onClick={() => setOpen(false)}>Admin Dashboard</Link>
                  )}
                  <Button variant="outline" className="rounded-full" onClick={handleLogout}>Sign out</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="rounded-full" onClick={() => { setOpen(false); navigate("/login"); }}>Sign in</Button>
                  <Button className="rounded-full" onClick={() => { setOpen(false); navigate("/register"); }}>Get Started</Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
